import React, {
  useCallback, useEffect, useState,
} from 'react';
import { connect } from 'react-redux';
import {
  Modal, Form, Input, Select, InputNumber, Alert, Typography, Row, Col, Icon, Button, Spin,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';
import {
  cancelEditVehicle,
  requestAddVehicle, requestDeleteVehicle,
  requestUpdateVehicle,
  requestVehicleAllUsage,
} from '../../../../state/ui/configuration/vehicles/vehicleUiActions';
import {
  selectEditState, selectModelTypes, selectModelTypesRequest, selectVehicleDiagramsRequest, selectVehicleDiagrams,
  selectSubmitVehicleDetailRequest,
} from '../../../../state/ui/configuration/vehicles/vehicleUiReducer';
import { filterSelectField } from '../inspection/config-util';
import { selectVehicle } from '../../../../state/domain/vehicles/vehicleReducer';
import VehicleDiagram from "../../../../components/diagram/VehicleDiagram";
import ConfirmButton from "../../../../components/buttons/ConfirmButton";
import {getDateDisplay} from "../../bynx/packageUtil";
import {getDefaultLocale} from "../../../../configWeb";

const { Text } = Typography;

/**
 * The field for the registration of the vehicle label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const RegNoField = injectIntl(({
  form, vehicle, intl, checkForDuplicates, isCreating,
}) => {
  return (
    <Form.Item required label={<FormattedMessage id="regNumber" />}>
      {
        form.getFieldDecorator('regNumber', {
          initialValue: vehicle ? vehicle.regNumber : undefined,
          rules: [
            { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
            {
              validator(rule, value, callback) {
                if (value !== (vehicle ? vehicle.regNumber : '') && checkForDuplicates(value)) {
                  callback(intl.formatMessage({ id: 'vehicleRegInActiveUse' }));
                }
                callback();
              },
            },
          ],
        })(
          <Input
            type="text"
            maxLength={20}
            allowClear
            placeholder={intl.formatMessage({ id: 'enter' })}
            disabled={!isCreating && vehicle.inAnyUse}
          />,
        )
      }
    </Form.Item>
  );
});

/**
 * The field for the description label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const VehicleDescriptionField = injectIntl(({
  form, vehicle, intl,
}) => (
  <Form.Item required label={<FormattedMessage id="vehicleDescription" />}>
    { form.getFieldDecorator('vehicleDescription', {
      initialValue: vehicle ? vehicle.vehicleDescription : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Input
        type="text"
        maxLength={250}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the model types linked to the vehicle.
 * The rules are injected into the form using the form field decorator.
 */
const ModelTypeField = injectIntl(({
  form, vehicle, modelTypes, intl, loading = false, reJigVehicleDiagramList, isCreating,
}) => (
  <Form.Item required label={<FormattedMessage id="modelType" />}>
    {
      form.getFieldDecorator('modelTypeCode', {
        initialValue: vehicle && vehicle.modelTypeCode ? vehicle.modelTypeCode : undefined,
        rules: [
          { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        ],
      })(
        <Select
          showArrow
          placeholder={<FormattedMessage id="selectOne" />}
          loading={loading}
          filterOption={filterSelectField}
          onChange={reJigVehicleDiagramList}
          disabled={!isCreating && vehicle.inAnyUse}
        >
          {
            modelTypes.map(modelType => (
              <Select.Option key={modelType.modelTypeCode} value={modelType.modelTypeCode}>
                {modelType.modelTypeDescription}
              </Select.Option>
            ))
            }
        </Select>,
      )
    }
  </Form.Item>
));

const ShowVehicleDiagram = ({
                              vehicleDiagramCode
                            }) => (
    vehicleDiagramCode && !isNaN(vehicleDiagramCode) && vehicleDiagramCode > 0
        ? <VehicleDiagram
            diagramCode={vehicleDiagramCode}
            className="show-vehicle-diagram"
            viewOnly={true}
          />
        : <Icon type="car" style={{ fontSize: '36px', opacity: '0.4' }} />
);

/**
 * The field for the vehicle diagram label. It is derived based on the model type selected.
 * The rules are injected into the form using the form field decorator.
 */
const VehicleDiagramField = injectIntl(({
  form, vehicle, vehicleDiagrams, intl, loading = false, isCreating, allVehicleDiagrams, setVehicleDiagramCode
}) => (
  <Form.Item required label={<FormattedMessage id="vehicleDiagram" />}>
    { form.getFieldDecorator('vehicleDiagramCode', {
      initialValue: vehicle && vehicle.vehicleDiagramCode ? vehicle.vehicleDiagramCode : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Select
        showArrow
        placeholder={<FormattedMessage id={ (form.getFieldValue('modelTypeCode') === undefined) ? 'selectModelTypeFirst' : 'selectOne' } />}
        loading={loading}
        filterOption={filterSelectField}
        onChange={(value) => {
          setVehicleDiagramCode(parseInt(value.toString()));
        }}
        optionLabelProp="label"
        dropdownClassName="dropdown-diagram-popup"
        disabled={((!isCreating && vehicle.inAnyUse) || (form.getFieldValue('modelTypeCode') === undefined))}
      >
      {
        vehicleDiagrams.map(vd => (
          <Select.Option key={vd.vehicleDiagramCode} value={vd.vehicleDiagramCode} label={vd.description}>
            <Row type={"flex"} align={"middle"} className="dropdown-diagram-row">
              <Col span={12}>
                <Text>{vd.description}</Text>
              </Col>
              <Col span={12}>
                <VehicleDiagram
                    diagramCode={vd.vehicleDiagramCode}
                    className="dropdown-vehicle-diagram"
                    viewOnly={true}
                />
              </Col>
            </Row>
          </Select.Option>
        ))
      }
      </Select>,
    )
  }
  </Form.Item>
));

/**
 * The field for the last odometer reading label. Allows clear, and max length set.
 * The rules are injected into the form using the form field decorator.
 */
const OdoReadingField = injectIntl(({
  form, vehicle, intl, locale,
}) => (
  <Form.Item label={<FormattedMessage id="vehicleDetailsOdo" />}>
    { form.getFieldDecorator('lastOdoReading', {
      initialValue: vehicle ? vehicle.lastOdoReading : undefined,
      rules: [
        {
          validator(rule, value, callback) {  //input only allows numbers, this stops decimals
            if ((value - Math.floor(value)) !== 0) {
              callback(intl.formatMessage({ id: 'wholeNumberRequired' }));
            }
            callback();
          },
        },
      ],
    })(
      <InputNumber
        type="number"
        min={0}
        max={9999999}
        maxLength={7}
        placeholder={intl.formatMessage({ id: 'enter' })}
        style={{ width: '150px' }}
      />,
    )}
    &nbsp;&nbsp;
    <Text>{(vehicle && vehicle.lastOdoReadingDate) ? getDateDisplay(vehicle.lastOdoReadingDate,locale) : ''}</Text>
  </Form.Item>
));


const EditVehicle = injectIntl(Form.create({ name: 'edit-vehicle' })(({
  intl, form, visible, isLoading, vehicle, vehicleId, onCreate, onUpdate, onDelete, onCancel, loadVehicleUsage,
  modelTypesLoading, modelTypes, vehicleDiagramsLoading, vehicleDiagrams, sessionUser,
  isSubmitting, validationBox, descValidator, ...rest
}) => {
  const isCreating = !vehicle;
  const titleId = isCreating ? 'createVehicle' : 'editVehicle';
  const buttonId = 'save';
  const { resetFields } = form;

  const [vehicleDiagramCode, setVehicleDiagramCode] = useState(undefined);

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) {
      resetFields();

      if (vehicle && vehicle.modelTypeCode) {
        const modelTypeSelected = modelTypes.find(mt => vehicle.modelTypeCode.toString() === `${mt.modelTypeCode}`);
        const filteredArray = vehicleDiagrams.filter(diagram => (
            modelTypeSelected.vehicleDiagramCodes.find(modelTypeDiagramCode => modelTypeDiagramCode === diagram.vehicleDiagramCode)
        ));
        setLinkedDiagrams(filteredArray);
      }

      setVehicleDiagramCode(vehicle ? vehicle.vehicleDiagramCode : undefined);
    }
  }, [visible, resetFields, vehicle, modelTypes, vehicleDiagrams, loadVehicleUsage]);

  useEffect(() => {
    if (!isCreating) {
      loadVehicleUsage(vehicleId);
    }
  }, [isCreating, vehicleId, loadVehicleUsage]);

  // On form submit.
  const submitVehicle = useCallback(() => {
    form.validateFields((err, values) => {
      if (err) return; // Nothing more to do.

      if (isCreating) {
        const newVehicle = {
          ...values,
          regNumber: values.regNumber.toUpperCase(),
          lastOdoReadingDate: (values.lastOdoReading || values.lastOdoReading === 0) ? new Date().toISOString() : null,
          active: true
        };

        onCreate(newVehicle);
      } else {
        const updatedVehicle = {
          vehicleId: vehicle.vehicleId,
          modelTypeCode: values.modelTypeCode,
          vehicleDiagramCode: values.vehicleDiagramCode,
          lastOdoReadingDate: (values.lastOdoReading || values.lastOdoReading === 0) ? new Date().toISOString() : null,
          regNumber: values.regNumber.toUpperCase(),
          vehicleDescription: values.vehicleDescription,
          lastOdoReading: values.lastOdoReading,
          active: true,
        };

        onUpdate(updatedVehicle);
      }
    });
  }, [form, isCreating, onCreate, onUpdate, vehicle]);

  const deleteVehicleButton = useCallback((vehId, vehReg) => {
    onDelete(vehId, vehReg);
  }, [onDelete]);

  const [linkedDiagrams, setLinkedDiagrams] = useState([]);

  function setAvailableDiagramsForChosenModelType(value) {
    const modelTypeSelected = modelTypes.find(mt => value.toString() === `${mt.modelTypeCode}`);
    const filteredArray = vehicleDiagrams.filter(diagram => (
      modelTypeSelected.vehicleDiagramCodes.find(modelTypeDiagramCode => modelTypeDiagramCode === diagram.vehicleDiagramCode)
    ));
    setLinkedDiagrams(filteredArray);
    form.setFieldsValue({vehicleDiagramCode: undefined}); // if user changes model type selected, then system should reset diagram code forcing user to re-select the vehicle diagram
    setVehicleDiagramCode(undefined);
  }

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  return (
    <Modal
      destroyOnClose
      title={<FormattedMessage id={titleId} />}
      visible={visible}
      onOk={submitVehicle}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      footer={
        <Row type="flex" align="middle" justify="space-between">
          <div>
            {
              (!isCreating && !vehicle.inAnyUse)
              ?
              <ConfirmButton type="danger" onClick={() => deleteVehicleButton(vehicleId, vehicle.regNumber)}>
                <FormattedMessage id="delete" />
              </ConfirmButton>
              : <></>
            }
          </div>
          <div>
            <Button onClick={onCancel}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button key="submit" type="primary" loading={isLoading} onClick={submitVehicle}>
              <FormattedMessage id={buttonId} />
            </Button>
          </div>
        </Row>
      }
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitVehicle(); }}>
        <ViaValidationAlert validationError={validationBox} />
        {
          (!isCreating && vehicle.inAnyUse) ? (<Alert type="info" description={<FormattedMessage id="vehicleCannotBeFullyAmended" />} showIcon />) : ''
        }
        <RegNoField form={form} vehicle={vehicle} checkForDuplicates={descValidator} isCreating={isCreating} />
        <VehicleDescriptionField form={form} vehicle={vehicle} />
        <ModelTypeField
          form={form}
          vehicle={vehicle}
          modelTypes={modelTypes}
          modelTypesLoading={modelTypesLoading}
          reJigVehicleDiagramList={setAvailableDiagramsForChosenModelType}
          isCreating={isCreating}
        />
        <VehicleDiagramField
          form={form}
          vehicle={vehicle}
          vehicleDiagrams={linkedDiagrams}
          vehicleDiagramsLoading={vehicleDiagramsLoading}
          isCreating={isCreating}
          allVehicleDiagrams={vehicleDiagrams}
          setVehicleDiagramCode={setVehicleDiagramCode}
        />
        <Row type="flex" align="middle" justify="center" className="show-vehicle-diagram">
          <ShowVehicleDiagram vehicleDiagramCode={vehicleDiagramCode} />
        </Row>
        {isSubmitting
            ? <Spin />
            : <OdoReadingField form={form} vehicle={vehicle} locale={userLocale} />
        }
      </Form>
    </Modal>
  );
}));

const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const modelTypesRequest = selectModelTypesRequest(state);
  const modelTypes = selectModelTypes(state);
  const vehicleDiagramsRequest = selectVehicleDiagramsRequest(state);
  const vehicleDiagrams = selectVehicleDiagrams(state);
  const submitRequest = selectSubmitVehicleDetailRequest(state);

  return {
    visible: editState.isEditing,
    isLoading: vehicleDiagramsRequest ? vehicleDiagramsRequest.isLoading : false,
    vehicle: editState.vehicleId ? selectVehicle(state, editState.vehicleId) : null,
    vehicleId: editState.vehicleId,
    modelTypesLoading: modelTypesRequest ? modelTypesRequest.isLoading : false,
    modelTypes: modelTypes || [],
    vehicleDiagramsLoading: vehicleDiagramsRequest ? vehicleDiagramsRequest.isLoading : false,
    vehicleDiagrams: vehicleDiagrams || [],
    showConfirmMessage: editState.showPopConfirm,
    editState,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  onCreate: newVehicle => dispatch(requestAddVehicle(newVehicle)),
  onUpdate: updatedVehicle => dispatch(requestUpdateVehicle(updatedVehicle)),
  onDelete: (deleteVehicleId, deleteRegNo) => dispatch(requestDeleteVehicle(deleteVehicleId, deleteRegNo)),
  onCancel: () => dispatch(cancelEditVehicle()),
  loadVehicleUsage: vehicleId => dispatch(requestVehicleAllUsage(vehicleId)),  //vehicle used on any inspections
});

export default connect(mapStateToProps, mapDispatchToProps)(EditVehicle);
